import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IDIncomeVerificationStatus, NotificationType } from '@common/entities';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { NotificationStatus } from 'enums/notification-status';
import { enqueueNotification, NotificationPayload } from 'state/slices/notificationsSlice';
import { RootState } from 'state/store';

import { getNotificationsForApplication, GetNotificationsParams } from 'services';

interface UseNotificationsOptions {
  applicationResponseId?: string;
  unreadOnly?: boolean;
  type?: string;
  enabled?: boolean;
  staleTime?: number;
  retry?: number;
  refetchInterval?: number | false;
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
}

interface UseNotificationsReturn extends Omit<UseQueryResult, 'data'> {
  notifications: NotificationPayload[];
  refetchNotifications: () => void;
}

const getIDIncomeVerificationNotificationStatus = (status: any): NotificationStatus => {
  if (status === IDIncomeVerificationStatus.Success) {
    return NotificationStatus.SUCCESS;
  }
  if (status === IDIncomeVerificationStatus.ReInitiated) {
    return NotificationStatus.INFO;
  }
  return NotificationStatus.WARNING;
};

// Helper function to map API NotificationType to UI NotificationStatus
const mapNotificationTypeToStatus = (type: NotificationType, data: any): NotificationStatus => {
  switch (type) {
    case NotificationType.General:
      return NotificationStatus.INFO;
    case NotificationType.ScheduledChargeChange:
    case NotificationType.SpecialValidationFailed:
      return NotificationStatus.WARNING;
    case NotificationType.PlaidIDVerification:
    case NotificationType.PlaidIncomeVerification:
      return getIDIncomeVerificationNotificationStatus(data?.metadata?.currentStatus);
    case NotificationType.ApplicationCompletion:
      return NotificationStatus.SUCCESS;
    default:
      return NotificationStatus.INFO;
  }
};

export const useNotifications = ({
  applicationResponseId,
  unreadOnly,
  type,
  enabled = true,
  staleTime = 0, // Always refetch to get latest notifications
  retry = 2,
  refetchInterval,
  onSuccess,
  onError,
}: UseNotificationsOptions = {}): UseNotificationsReturn => {
  const dispatch = useDispatch();
  const notifications = useSelector((state: RootState) => state.notifications.queue);
  const { isPreviewMode = false } = useSelector((state: RootState) => state.auth);
  const { pageId } = useParams();

  // Get applicationResponseId from Redux state if not provided
  const currentResponseId = useSelector(
    (state: RootState) =>
      applicationResponseId || state.application.applicant?.currentResponse?.responseId,
  );

  const fetchNotifications = useCallback(async () => {
    if (!currentResponseId) {
      throw new Error('Application Response ID is required to fetch notifications');
    }

    const params: GetNotificationsParams = {
      applicationResponseId: currentResponseId,
    };

    if (unreadOnly !== undefined) params.unreadOnly = unreadOnly;
    if (type !== undefined) params.type = type;

    const response = await getNotificationsForApplication(params);
    return response;
  }, [currentResponseId, unreadOnly, type]);

  const queryEnabled = enabled && !!currentResponseId && !isPreviewMode;

  const queryResult = useQuery({
    queryKey: ['notifications', currentResponseId, unreadOnly, type, pageId],
    queryFn: fetchNotifications,
    enabled: queryEnabled,
    staleTime,
    retry,
    refetchInterval,
    ...(onSuccess && { onSuccess }),
    ...(onError && { onError }),
  });

  // Update Redux store when data is fetched
  useEffect(() => {
    if (queryResult.data?.notifications) {
      // Clear existing notifications and add new ones
      queryResult.data.notifications.forEach((notification) => {
        // Check if notification already exists to avoid duplicates
        const existingNotification = notifications.find((n) => n.id === notification.id);
        if (!existingNotification) {
          const notificationPayload: NotificationPayload = {
            id: notification.id,
            title: notification.title,
            message: notification.message,
            status: mapNotificationTypeToStatus(notification.type, notification.metadata),
            date: notification.createdDate
              ? new Date(notification.createdDate).toISOString()
              : new Date().toISOString(),
            persist: true,
            read: !!notification.readDate,
            type: notification.type,
            metadata: notification.metadata,
          };
          dispatch(enqueueNotification(notificationPayload));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResult.data, dispatch]);

  return {
    ...queryResult,
    notifications: isPreviewMode ? [] : notifications,
    refetchNotifications: queryResult.refetch,
  };
};
